import { TableRow } from "./TableRow"
import React, { ReactNode, useEffect, useState } from "react"
import TableSkeleton from "./TableGridSkeleton"
import cn from "../../../../utils/classes"
import { ZIndex } from "../../../../utils/zIndex"

interface TableGridProps {
  headers: ReactNode[]
  rows: ReactNode[][]
  headerGroups?: ReactNode[]
  loading?: boolean
  tabIndex?: number
  onFocus?: () => void
  totalRowCount?: number
  firstDataRowIndex?: number
  tableId?: string
  ariaLabel?: string
  styleOverrides?: string
}

export const TableGrid: React.FC<TableGridProps> = ({
  headers,
  rows,
  headerGroups = [],
  loading = false,
  tabIndex = 0,
  onFocus,
  totalRowCount,
  firstDataRowIndex,
  tableId,
  ariaLabel,
  styleOverrides = "",
}) => {
  const [tableTabIndex, setTableTabIndex] = useState(tabIndex)

  useEffect(() => {
    setTableTabIndex(tabIndex)
  }, [tabIndex])

  return (
    <>
      {loading ? (
        <TableSkeleton />
      ) : (
        <div
          className={cn(
            "w-full min-w-0 !max-h-[75vh] overflow-auto bg-white border-[1px] border-solid border-gray-200 rounded-lg",
            styleOverrides,
          )}
        >
          <table
            id={tableId}
            className="w-full bg-white border-spacing-0"
            aria-label={ariaLabel ?? "Data table"}
            tabIndex={tableTabIndex}
            onFocus={(e) => {
              // Guard against React's synthetic onFocus bubbling up from child elements (e.g. edit/delete buttons).
              // Without this, clicking a button inside the table triggers onFocus on the table itself, which calls
              // setFocusedCell({ row: 0, col: 0 }) and forces focus to the first cell — scrolling the table back
              // to the left before the click resolves. We only want to initialize keyboard navigation when the
              // table element itself receives focus (i.e. the user tabs into the table).
              if (e.target === e.currentTarget) onFocus?.()
            }}
            aria-rowcount={totalRowCount}
          >
            <thead
              className="sticky top-0"
              style={{ zIndex: ZIndex.tableHeader }}
            >
              {headerGroups.length > 0 && (
                <TableRow
                  id="header-groups"
                  className="!rounded-lt-lg bg-white hover:bg-white [&>th]:bg-white"
                  aria-rowindex={totalRowCount !== undefined ? 1 : undefined}
                >
                  {headerGroups}
                </TableRow>
              )}
              <TableRow
                id="headers"
                className="bg-gray-50 !rounded-tl-lg [&>th]:!bg-gray-50"
                aria-rowindex={
                  totalRowCount !== undefined
                    ? headerGroups.length > 0
                      ? 2
                      : 1
                    : undefined
                }
              >
                {headers}
              </TableRow>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <TableRow
                  key={`row-${rowIndex}`} // TODO: this is not ideal, we should have a unique id for each row, but for now this will work
                  id={`row-${rowIndex}`}
                  className={cn({
                    "rounded-0 rounded-bl-lg !border-b-0":
                      rowIndex === rows.length - 1,
                  })}
                  aria-rowindex={
                    firstDataRowIndex !== undefined
                      ? firstDataRowIndex + rowIndex
                      : undefined
                  }
                >
                  {row}
                </TableRow>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
